import React, { useState } from 'react';
import { Upload, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const FinalDownsReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.csv'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) || !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a CSV file only');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);

      // Send file to server for processing using the new generic endpoint
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/finaldowns`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderReport = () => {
    if (!report) return null;

    return (
      <Box sx={{ mt: 2, bgcolor: 'white', minHeight: '100vh' }}>
        {/* Report Content */}
        <Box sx={{ p: 1 }}>

        {/* Complete Final Downs Report - Header Section */}
        <Box sx={{ mb: 2 }} data-section="report-header-section">
          <ReportHeader 
            title="FINAL DOWNS ANALYSIS"
            logos={report.logos}
            fileName={file?.name}
            subtitle="Situations where defense knows offense MUST convert or give up possession"
          />
        </Box>


        {/* Foundation Data Section */}
        {report.foundation && (
          <Box sx={{ mb: 2 }} data-section="foundation-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              FOUNDATION DATA
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Total Runs:</strong> {report.foundation.totalRuns || 0} ({report.foundation.runPercentage || 0}%)
                  {(report.foundation.runPercentage || 0) >= 80 && ' ❗ EXTREMELY VALUABLE RUN TENDENCY'}
                  {(report.foundation.runPercentage || 0) >= 70 && (report.foundation.runPercentage || 0) < 80 && ' ⚠️ VALUABLE RUN TENDENCY'}
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Total Passes:</strong> {report.foundation.totalPasses || 0} ({report.foundation.passPercentage || 0}%)
                  {(report.foundation.passPercentage || 0) >= 80 && ' ❗ EXTREMELY VALUABLE PASS TENDENCY'}
                  {(report.foundation.passPercentage || 0) >= 70 && (report.foundation.passPercentage || 0) < 80 && ' ⚠️ VALUABLE PASS TENDENCY'}
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Pass Completions:</strong> {report.foundation.completions || 0} of {report.foundation.totalPasses || 0} ({report.foundation.completionPercentage || 0}%)
                </Typography>
              </li>
            </Box>
          </Box>
        )}

        {/* Distance Analysis Section */}
        {report.distances && report.distances.length > 0 && (
          <Box sx={{ mb: 2 }} data-section="distance-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              DISTANCE BREAKDOWN & ANALYSIS
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              {report.distances.map((distance, index) => (
                <li key={index}>
                  <Typography variant="h6" component="span">
                    <strong>{distance.name}:</strong> {distance.totalPlays} plays
                  </Typography>
                </li>
              ))}
              <li>
                <Typography variant="h6" component="span">
                  <strong>Verification:</strong> {report.distances.reduce((sum, d) => sum + d.totalPlays, 0)} = {report.verification?.totalTrueFinalDowns || 0} ✓
                </Typography>
              </li>
            </Box>

            {/* Distance Details - 2 Column Layout */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {report.distances.map((distance, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Box sx={{ height: '100%' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', 
                      color: '#000', 
                      mb: 1,
                      bgcolor: '#f5f5f5',
                      p: 1,
                      borderLeft: '4px solid #1976d2',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}>
                      {distance.name} ({distance.totalPlays} plays):
                    </Typography>
                    
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0, 
                      ml: 1,
                      '& li': {
                        mb: 0.3,
                        '&:before': {
                          content: '"- "',
                          fontWeight: 'bold'
                        }
                      }
                    }}>
                      <li>
                        <Typography variant="body2" component="span">
                          <strong>3rd downs:</strong> {distance.thirdDowns || 0} | <strong>4th downs:</strong> {distance.fourthDowns || 0}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" component="span">
                          <strong>Runs:</strong> {distance.runs || 0} ({distance.runPercentage || 0}%)
                          {(distance.runPercentage || 0) >= 80 && ' ❗'}
                          {(distance.runPercentage || 0) >= 70 && (distance.runPercentage || 0) < 80 && ' ⚠️'}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" component="span">
                          <strong>Passes:</strong> {distance.passes || 0} ({distance.passPercentage || 0}%)
                          {(distance.passPercentage || 0) >= 80 && ' ❗'}
                          {(distance.passPercentage || 0) >= 70 && (distance.passPercentage || 0) < 80 && ' ⚠️'}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" component="span">
                          <strong>Pass Completions:</strong> {distance.completions || 0} of {distance.passes || 0} ({distance.completionPercentage || 0}%)
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body2" component="span">
                          <strong>Average Pass Depth:</strong> {distance.avgPassDepth || 'N/A'} yards
                        </Typography>
                      </li>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}


        {/* Pass Targets Section */}
        {report.passTargets && report.passTargets.length > 0 && (
          <Box sx={{ mb: 2 }} data-section="pass-targets-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              PASS TARGET ANALYSIS
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              {report.passTargets.map((target, index) => (
                <li key={index}>
                  <Typography variant="h6" component="span">
                    <strong>{target.receiver}:</strong> {target.targets} targets ({target.targetShare}% share), {target.completions}/{target.targets} completions ({target.completionPercentage}%), {target.avgDepth} avg depth
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}

        {/* Success Analysis Section */}
        {report.success && (
          <Box sx={{ mb: 2 }} data-section="success-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              SUCCESS ANALYSIS
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Overall Conversion Rate:</strong> {report.success.overallConversions}/{report.success.totalPlays} ({report.success.overallConversionRate}%)
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Run Conversion Rate:</strong> {report.success.runConversions}/{report.success.totalRuns} ({report.success.runConversionRate}%)
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Pass Conversion Rate:</strong> {report.success.passConversions}/{report.success.totalPasses} ({report.success.passConversionRate}%)
                </Typography>
              </li>
            </Box>

            {report.success.byDistance && report.success.byDistance.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 1 }}>
                  Conversion Rate by Distance:
                </Typography>
                
                <Box component="ul" sx={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0, 
                  ml: 2,
                  '& li': {
                    mb: 0.3,
                    '&:before': {
                      content: '"- "',
                      fontWeight: 'bold'
                    }
                  }
                }}>
                  {report.success.byDistance.map((distance, index) => (
                    <li key={index}>
                      <Typography variant="body1" component="span">
                        <strong>{distance.name}:</strong> {distance.conversions}/{distance.attempts} ({distance.conversionRate}%)
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Data Verification Section - Moved to bottom */}
        {report.verification && (
          <Box sx={{ mb: 2 }} data-section="verification-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              DATA VERIFICATION - FINAL DOWNS FILTER
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              <li>
                <Typography variant="h6" component="span">
                  <strong>Raw 3rd downs:</strong> {report.verification.raw3rdDowns || 0}
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>3rd downs excluded (followed by 4th):</strong> {report.verification.excluded3rdDowns || 0}
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>True 3rd downs included:</strong> {report.verification.true3rdDowns || 0}
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>All 4th downs:</strong> {report.verification.all4thDowns || 0}
                </Typography>
              </li>
              <li>
                <Typography variant="h6" component="span">
                  <strong>TOTAL FINAL DOWNS:</strong> {report.verification.totalTrueFinalDowns || 0}
                </Typography>
              </li>
            </Box>
          </Box>
        )}

        </Box> {/* End Report Content */}
      </Box>
    );
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.FINAL_DOWNS,
      'final-downs-report.pdf',
      setError
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/reports"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Reports
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FileText size={32} />
        Final Downs Analysis Report
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload your CSV file containing play-by-play data to analyze true final down tendencies. 
        This report identifies situations where the defense knows the offense MUST convert or give up possession.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Upload CSV Data</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a CSV file containing your play-by-play data. The file should include columns for down, distance, 
            run/pass, field position, personnel, and play results.
          </Typography>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="csv-file-upload"
            disabled={loading}
          />
          <label htmlFor="csv-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose CSV File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText />}
          size="large"
        >
          {loading ? 'Generating Report...' : 'Generate Final Downs Report'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={clearAll}
          disabled={loading}
        >
          Clear All
        </Button>
      </Box>

      {loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Processing your data...</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The server is analyzing your CSV data and generating the true final downs report. This may take a few moments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Generated Report</Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={saveReportAsPDF}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="styled-report-content">
            {renderReport()}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FinalDownsReportGenerator;